import React from "react";


/**
 * DataTableRow
 *
 * The table row component renders a single row in the table body.
 *
 * @param {React.ReactNode} [children] - The children of the row. If not
 * provided, the cells prop will be used to generate the cells.
 * @param {Object[]} [cells] - An array of objects, each containing a
 * `style` and a `value` property.
 * @param {Function} [onRowClick] - A function that is called when the
 * row is clicked. The function will be passed the row data and the
 * row index.
 * @param {Object} [rowData] - The row data for this row.
 * @param {number} [rowIndex] - The index of the row in the table.
 * @param {Object} [style] - The style object to apply to the row.
 * @param {string} [className] - The class name to apply to the row.
 * @param {Object} [rest] - Any additional props will be passed to the
 * underlying <tr> element.
 * @returns {React.ReactNode} The rendered table row.
 */
const DataTableRow = ({ children, cells, onRowClick, rowData, rowIndex, style, className, ...rest }) => {
  const isClickable = !!onRowClick;

  return (
    <tr
      onClick={() => onRowClick && onRowClick(rowData, rowIndex)}
      style={{
        cursor: isClickable ? "pointer" : "default",
        transition: "background-color 0.2s ease-in-out",
        ...style
      }}
      onMouseEnter={(e) => {
        if (isClickable) e.currentTarget.style.backgroundColor = "#f8f9fa";
      }}
      onMouseLeave={(e) => {
        if (isClickable) e.currentTarget.style.backgroundColor = "";
      }}
      className={className}
      {...rest}
    >
      {children ??
        (Array.isArray(cells)
          ? cells.map((cell, i) => <td key={i} style={{ padding: "8px 12px" }}>{cell}</td>)
          : null)}
    </tr>
  );
};

export default DataTableRow;
